/**
 * Pure utility functions for Vitamin D screening score
 * NO external dependencies - works anywhere!
 *
 * Questions used:
 *   q1  Age > 50                (derived from age)          +0.5
 *   q2  BMI >= 30               (asked directly, yes/no)    +1
 *   q5  Time outdoors           (more_30 / less_30 / negligible)  0 / 2 / 3
 *   q6  Sunscreen               (yes)                       +1
 *   q7  Pollution / fog         (yes)                       +1
 *   q9  Low dairy intake        (yes)                       +1
 *   q12 Osteoporosis / fracture (yes)                       +1
 *   q13-q17 Symptoms            (no / sometimes / often)    0 / 0.25 / 0.5 each
 *   q18 Vitamin D supplement    (yes)                       -5
 *
 * Maximum possible score: 11   Minimum: 0 (negative scores are floored to 0)
 */

// Score below this = Adequate, at or above = Inadequate
export const ADEQUACY_CUTOFF = 2.5;
export const MAX_SCORE = 10;

/**
 * Determine risk level based on total score
 * @param totalScore - Total questionnaire score
 * @returns Risk level string
 */
export const getRiskLevel = (totalScore) => {
  return totalScore < ADEQUACY_CUTOFF ? "Adequate" : "Inadequate";
};

/**
 * Calculate screening score based on questionnaire responses
 * @param formData - Questionnaire responses (q2, q5, q6, q7, q9, q12-q18)
 * @param age - Patient age
 * @returns Total score
 */
export const calculateSectionBScore = (formData, age, bmi = 0) => {
  let score = 0;

  // Q1: Age > 50?
  if (formData.q1 === "yes" || age > 50) score += 0.5;

  // // Q2: BMI >= 30? (asked directly, or calculated if bmi is passed in)
  // if (formData.q2 === "yes" || bmi >= 30) score += 1;

  // Q5: Time outdoors
  const outdoorScores = {
    more_30: 0,
    less_30: 2,
    negligible: 3,
  };
  score += outdoorScores[formData.q5] || 0;

  // Q6: Sunscreen (Yes/No)
  if (formData.q6 === "yes") score += 1;

  // Q7: Pollution (Yes/No)
  if (formData.q7 === "yes") score += 1;

  // Q9, Q12: Yes/No (each Yes = +1)
  ["q9", "q12"].forEach((q) => {
    if (formData[q] === "yes") score += 1;
  });

  // Q13-Q17: No/Sometimes/Often (0, 0.25, 0.5)
  ["q13", "q14", "q15", "q16", "q17"].forEach((q) => {
    if (formData[q] === "sometimes") score += 0.25;
    else if (formData[q] === "often") score += 0.5;
  });

  // Q18: Vitamin D supplementation (Yes = -5)
  if (formData.q18 === "yes") score -= 5;

  // Ensure score is not negative
  return Math.max(0, score);
};

/**
 * Generate assessment report as a formatted string
 * All fields are optional so both forms can use it.
 * @param patientData - Patient information
 * @returns Formatted assessment report
 */
export const generateAssessmentReport = (patientData) => {
  const comorbidities = patientData.comorbidities || [];
  const comorbidityText = comorbidities.length
    ? comorbidities.join("\n")
    : "• None";
  const hasHeight = patientData.height_feet !== undefined;
  const hasBmi = typeof patientData.bmi === "number";

  return `
--------------------------
 Vitamin D Assessment
--------------------------
${patientData.name ? `Patient: ${patientData.name}` : ""}
${patientData.phone ? `Phone: ${patientData.phone}` : ""}
Age: ${patientData.age}${patientData.gender ? `   Gender: ${patientData.gender}` : ""}
${hasHeight ? `Height: ${patientData.height_feet}'${patientData.height_inches}"` : ""}
${patientData.weight_kg !== undefined ? `Weight: ${patientData.weight_kg}kg` : ""}
${hasBmi ? `BMI: ${patientData.bmi.toFixed(1)}` : ""}
${comorbidities.length || patientData.comorbidities ? `\nComorbidities:\n${comorbidityText}\n` : ""}
Total Score: ${patientData.totalScore}
Risk Level: ${patientData.riskLevel}

--------------------------
ADEQUACY GUIDE:
< ${ADEQUACY_CUTOFF}   → Adequate
≥ ${ADEQUACY_CUTOFF}   → Inadequate
--------------------------
This is a screening test 
and not a substitute for
a diagnostic test.Please 
consult your doctor for 
interpretation and 
appropriate treatment.`;
};

/**
 * Calculate BMI from weight and height
 * @param weightKg - Weight in kilograms
 * @param heightM - Height in meters
 * @returns BMI rounded to 2 decimal places
 */
export const calculateBMI = (weightKg, heightM) => {
  if (heightM <= 0 || weightKg <= 0) return 0;
  return Math.round((weightKg / (heightM * heightM)) * 100) / 100;
};

/**
 * Get badge variant based on risk level (for UI styling)
 */
export const getRiskBadgeVariant = (riskLevel) => {
  switch (riskLevel) {
    case "Adequate":
      return "secondary";
    case "Inadequate":
      return "destructive";
    default:
      return "outline";
  }
};

/**
 * Validate patient form data
 */
export const validatePatientFormData = (formData) => {
  return !!(
    formData.initials?.trim() &&
    formData.age &&
    Number(formData.age) > 0 &&
    Number(formData.age) <= 120 &&
    formData.gender &&
    formData.weight_kg &&
    Number(formData.weight_kg) > 0
  );
};

/**
 * Debounce function for performance optimization
 */
export const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
